import {Component, OnInit, signal} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {combineLatest, filter, startWith, Subscription, switchMap} from 'rxjs';
import {AppUtils} from './core/utils/app.utils';
import {COOKIE} from './core/utils/app.constants';
import {ClaimTenant, VodoriJwt} from './core/utils/app.model';
import {jwtDecode} from "jwt-decode";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit {

  loading = signal(true);
  private subscription: Subscription = new Subscription();

  constructor(private authService: AuthService) {
  }

  ngOnInit() {
    console.log('LOGIN: Init')
    if (AppUtils.isAPublicPage()) {
      this.loading.set(false);
    } else {
      this.authenticate();
    }
  }

  authenticate() {
    this.subscription.add(
      this.authService.isAuthenticated$.subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          console.log('LOGIN: 🔒 - User not authenticated.');
          AppUtils.getCookie(COOKIE.CLOUD_TNTS)?.split(',').forEach(tnt => {
            const et = tnt.split('/');
            AppUtils.deleteCookie(COOKIE.CLOUD_JWT, `/${et[0]}/cloud/${et[1]}/flow-config`);
          })
          AppUtils.deleteCookie(COOKIE.CLOUD_TNTS);
        }
      })
    );

    this.subscription.add(
      combineLatest([
        this.authService.isLoading$,
        this.authService.isAuthenticated$,
      ]).pipe(
        filter(([isLoading, isAuthenticated]) => !isLoading && isAuthenticated),
        switchMap(_ => {
          return combineLatest([
            this.authService.user$,
            this.authService.getAccessTokenSilently(),
            this.authService.appState$.pipe(startWith(null))
          ]);
        })
      ).subscribe({
        next: ([user, jwtToken, appState]) => {
          console.log('LOGIN: 🔐 - User authenticated');
          const cloudCallbackUri = AppUtils.callbackPathFromCookieOrAppState(
            AppUtils.getCookie(COOKIE.CLOUD_CALLBACK_URI),
            appState?.target
          );
          const decodedToken: VodoriJwt = jwtDecode(jwtToken);
          AppUtils.clearCloudSessionStorageState();
          let canAccessCallbackUri = AppUtils.canAccessCallbackUri(cloudCallbackUri, decodedToken);

          if (cloudCallbackUri && canAccessCallbackUri) {
            const callbackRedirectUri = this.prepareCloudCallbackRedirect(cloudCallbackUri, decodedToken, jwtToken);
            console.log(`LOGIN: ➡️ Redirect to cloudCallbackUri: ${callbackRedirectUri}`);
            window.location.replace(callbackRedirectUri);
            return;
          } else {
            const defaults: string = decodedToken['https://vodori.com/def'];
            this.setEnvironmentsCookie(decodedToken);

            if (defaults != null) {
              console.log(`LOGIN: ➡️ No callbackURI cookie. Using defaults: ${defaults}`);
              window.location.replace(`${window.origin}/${defaults}`);
            } else {
              let presumedRoute = AppUtils.getPresumedRouteForToken(decodedToken);
              if (presumedRoute) {
                console.log(`LOGIN: ➡️ No cloudCallbackUri, no defaults, routing to ${presumedRoute}`)
                window.location.replace(`${window.origin}/${presumedRoute}`);
              } else {
                console.log(`LOGIN: 🚫 No cloudCallbackUri, no defaults, no valid persona.`)
                window.location.replace(`/login/public/error/403`);
              }
            }
          }

          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);

          if (error?.message !== "Login required") {
            console.error('LOGIN: 🚫 - Error retrieving access token', error);
          }
        }
      })
    );
  }

  setEnvironmentsCookie(decodedToken: VodoriJwt) {
    const tenants: ClaimTenant[] = decodedToken['https://vodori.com/tnts'];
    const ets = tenants.map(ten => (ten.et)).join(',')
    AppUtils.setCookie(COOKIE.CLOUD_TNTS, ets);
  }

  prepareCloudCallbackRedirect(cloudCallbackUri: string, decodedToken: VodoriJwt, jwtToken: string): string {
    this.setEnvironmentsCookie(decodedToken);

    const aiJwtCookiePath = AppUtils.jwtCookiePathForCallbackUri(cloudCallbackUri);
    if (aiJwtCookiePath) {
      AppUtils.setCookie(COOKIE.CLOUD_JWT, jwtToken, 1, aiJwtCookiePath);
    }
    AppUtils.deleteCookie(COOKIE.CLOUD_CALLBACK_URI);
    return window.origin + cloudCallbackUri;
  }
}
